import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { useLevelStore } from '../store/levelStore';
import { FiPlay, FiStar, FiTarget, FiSettings, FiLogOut, FiAward } from 'react-icons/fi';
import { BsTrophy } from 'react-icons/bs';

const HomePage = () => {
  const { user, logout } = useAuthStore();
  const { levels, progress, fetchLevels, fetchProgress } = useLevelStore();

  useEffect(() => {
    // Only fetch the first page to get count
    fetchLevels(1);
    fetchProgress();
  }, []);

  const totalLevels = useLevelStore(state => state.totalLevels);
  const completedCount = progress.filter(p => p.completed).length;
  const totalStars = progress.reduce((sum, p) => sum + p.stars, 0);

  const handleLogout = () => {
    logout();
  };

  return (
    <div className="home-page">
      {/* Header */}
      <header className="home-header">
        <div className="header-content container">
          <h1 className="logo text-gradient glow-text">Match-3</h1>
          <div className="header-actions">
            <Link to="/leaderboard" className="btn btn-secondary" style={{ fontSize: '0.8rem', padding: '6px 12px' }}>
              <BsTrophy /> <span style={{ marginLeft: 4 }}>Рейтинг</span>
            </Link>
            {user?.role === 'ADMIN' && (
              <Link to="/admin" className="btn btn-secondary">
                <FiSettings /> Админ
              </Link>
            )}
            <div className="user-info">
              <span className="username">{user?.username}</span>
              <button onClick={handleLogout} className="btn btn-secondary btn-icon">
                <FiLogOut />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container">
        {/* Hero Section */}
        <section className="hero animate-slide-up">
          <div className="hero-content">
            <h2 className="text-gradient">Готовы играть?</h2>
            <p>Собирайте камни, проходите уровни и зарабатывайте звёзды!</p>
            <Link to="/levels" className="btn btn-primary btn-lg animate-pulse">
              <FiPlay /> Играть
            </Link>
          </div>
          <div className="hero-decoration"></div>
        </section>

        {/* Stats Section */}
        <section className="stats-grid animate-slide-up">
          <div className="stat-card card">
            <div className="stat-icon">
              <FiTarget />
            </div>
            <div className="stat-info">
              <span className="stat-value">{user?.currentLevel || 1}</span>
              <span className="stat-label">Текущий уровень</span>
            </div>
          </div>

          <div className="stat-card card">
            <div className="stat-icon">
              <FiStar />
            </div>
            <div className="stat-info">
              <span className="stat-value">{totalStars}</span>
              <span className="stat-label">Звёзд заработно</span>
            </div>
          </div>

          <div className="stat-card card">
            <div className="stat-icon">
              <FiAward />
            </div>
            <div className="stat-info">
              <span className="stat-value">{completedCount}/{levels.length}</span>
              <span className="stat-label">Уровней пройдено</span>
            </div>
          </div>
        </section>

        {/* Quick Play Section */}
        <section className="quick-play animate-slide-up">
          <h3>Быстрая игра</h3>
          <div className="level-preview card">
            <div className="level-info">
              <h4>Уровень {user?.currentLevel || 1}</h4>
              <p>
                {levels.find(l => l.levelNumber === (user?.currentLevel || 1))?.name || 'Следующий уровень'}
              </p>
            </div>
            <Link
              to={`/game/${user?.currentLevel || 1}`}
              className="btn btn-primary"
            >
              <FiPlay /> Играть
            </Link>
          </div>
        </section>
      </main>

      <style>{`
        .home-page {
          min-height: 100vh;
          padding-bottom: var(--spacing-2xl);
        }
        .home-header {
          background: var(--bg-card);
          backdrop-filter: blur(10px);
          border-bottom: 1px solid var(--border-color);
          padding: var(--spacing-md) 0;
          position: sticky;
          top: 0;
          z-index: 100;
        }
        .header-content {
          display: flex;
          align-items: center;
          justify-content: space-between;
        }
        .logo {
          font-size: 1.75rem;
          font-weight: 800;
        }
        .header-actions {
          display: flex;
          align-items: center;
          gap: var(--spacing-md);
        }
        .user-info {
          display: flex;
          align-items: center;
          gap: var(--spacing-md);
        }
        .username {
          color: var(--text-secondary);
          font-weight: 500;
        }
        .btn-icon {
          padding: var(--spacing-sm);
        }
        .hero {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: var(--spacing-xl);
          align-items: center;
          padding: var(--spacing-2xl) 0;
        }
        .hero-content h2 {
          font-size: 3rem;
          font-weight: 800;
          margin-bottom: var(--spacing-md);
        }
        .hero-content p {
          font-size: 1.25rem;
          color: var(--text-secondary);
          margin-bottom: var(--spacing-xl);
        }
        .btn-lg {
          padding: var(--spacing-md) var(--spacing-xl);
          font-size: 1.25rem;
        }
        .hero-decoration {
          width: 100%;
          aspect-ratio: 1;
          background: radial-gradient(circle, var(--neon-purple) 0%, transparent 70%);
          opacity: 0.3;
          border-radius: 50%;
          filter: blur(40px);
        }
        .stats-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: var(--spacing-lg);
          margin-bottom: var(--spacing-2xl);
        }
        .stat-card {
          display: flex;
          align-items: center;
          gap: var(--spacing-lg);
          padding: var(--spacing-lg);
        }
        .stat-icon {
          width: 60px;
          height: 60px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: var(--gradient-primary);
          border-radius: var(--radius-md);
          font-size: 1.5rem;
        }
        .stat-info {
          display: flex;
          flex-direction: column;
        }
        .stat-value {
          font-size: 1.75rem;
          font-weight: 700;
          color: var(--text-primary);
        }
        .stat-label {
          color: var(--text-secondary);
          font-size: 0.875rem;
        }
        .quick-play {
          margin-bottom: var(--spacing-xl);
        }
        .quick-play h3 {
          font-size: 1.5rem;
          margin-bottom: var(--spacing-lg);
        }
        .level-preview {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: var(--spacing-lg);
        }
        .level-info h4 {
          font-size: 1.25rem;
          margin-bottom: var(--spacing-xs);
        }
        .level-info p {
          color: var(--text-secondary);
        }
        @media (max-width: 768px) {
          .hero {
            grid-template-columns: 1fr;
            text-align: center;
          }
          .hero-content h2 {
            font-size: 2rem;
          }
          .hero-decoration {
            display: none;
          }
          .stats-grid {
            grid-template-columns: 1fr;
          }
          .header-actions {
            gap: var(--spacing-sm);
          }
          .username {
            display: none;
          }
        }
      `}</style>
    </div>
  );
};

export default HomePage;
